import React, { Suspense } from 'react';
import { ApolloClient, ApolloProvider } from '@apollo/client';
import { useAuthStore } from '../stores/authStore';
import { Env, ILogger } from '@bloomays-lib/types.shared';
import { isAuth0TokenRefreshError } from '../services/authService';

export type BloomaysApolloProviderProps = {
  children: React.ReactNode;
  env: Env;
  loaderComponent?: React.ReactNode;
  logger: ILogger;
  useApolloStore: () => {
    apolloClient?: ApolloClient<unknown>;
    apolloClientError?: Error;
    initializeApolloClient: () => Promise<unknown>;
  };
};

const BloomaysApolloFactory = ({
  children,
  env,
  useApolloStore,
  logger,
  loaderComponent,
}: BloomaysApolloProviderProps) => {
  const { apolloClient, apolloClientError, initializeApolloClient } = useApolloStore();
  const auth = useAuthStore((state) => state.auth);

  if (apolloClientError) {
    // Check if the error is an Auth0 token expiration
    if (isAuth0TokenRefreshError(apolloClientError)) {
      logger.debug('Auth0 token expired detected in Apollo client initialization, redirecting to login');

      if (auth?.logout) {
        auth.logout();
        return loaderComponent;
      }
    }

    // For other errors, throw as before
    throw apolloClientError;
  }

  if (!apolloClient) {
    throw initializeApolloClient();
  }

  if (env === 'test') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const BloomaysApolloProvider = ({
  children,
  env,
  loaderComponent,
  useApolloStore,
  logger,
}: BloomaysApolloProviderProps) => {
  return (
    <Suspense fallback={loaderComponent}>
      <BloomaysApolloFactory env={env} useApolloStore={useApolloStore} logger={logger}>
        {children}
      </BloomaysApolloFactory>
    </Suspense>
  );
};

export default BloomaysApolloProvider;
export { BloomaysApolloProvider };
